import { useEffect, useMemo, useState } from "react";
import {
  FiAlertCircle,
  FiArrowLeft,
  FiCalendar,
  FiCheckCircle,
  FiChevronDown,
  FiChevronUp,
  FiClock,
  FiDollarSign,
  FiFileText,
  FiTrendingDown,
  FiTrendingUp,
  FiUsers,
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import PayrollStepper from "./components/PayrollStepper";
import { apiRequest } from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import PayrollPreviewAdvanceRecoverySetup from "./components/PayrollPreviewAdvanceRecoverySetup";
import HrmsFilters from '../../components/hrms/HrmsFilterPanel';
import { filterEmployeesForLeave as filterPayrollEmployees } from '../Leave/leaveEmployeeFilterUtils';

function previousIndiaMonth() {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
  }).formatToParts(new Date());

  const year = Number(parts.find((part) => part.type === "year")?.value);
  const month = Number(parts.find((part) => part.type === "month")?.value);

  const date = new Date(Date.UTC(year, month - 2, 1));

  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(
    2,
    "0",
  )}`;
}

function number(value) {
  return Number(value || 0);
}

function money(value) {
  return `₹${number(value).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function formatMonth(monthValue) {
  if (!monthValue || !/^\d{4}-\d{2}$/.test(monthValue)) return "—";

  const [year, month] = monthValue.split("-").map(Number);

  return new Intl.DateTimeFormat("en-IN", {
    month: "long",
    year: "numeric",
  }).format(new Date(year, month - 1, 1));
}

function formatDate(value) {
  if (!value) return "—";

  const [year, month, day] = String(value).slice(0, 10).split("-").map(Number);

  if (!year || !month || !day) return value;

  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(new Date(year, month - 1, day));
}

function formatMinutes(value) {
  const totalMinutes = Math.max(0, Math.round(number(value)));

  if (!totalMinutes) return "0 min";

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (!hours) return `${minutes} min`;
  if (!minutes) return `${hours}h`;

  return `${hours}h ${minutes}m`;
}

function formatTime(value) {
  if (!value) return "—";

  return new Intl.DateTimeFormat("en-IN", {
    timeZone: "Asia/Kolkata",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  }).format(new Date(value));
}

function SessionTimeline({ sessions = [] }) {
  if (!sessions.length)
    return <span className="text-gray-400">No sessions</span>;
  return (
    <div className="flex min-w-[220px] flex-wrap gap-1.5">
      {sessions.map((session, index) => (
        <span
          key={session.id || index}
          className="rounded-lg bg-gray-100 px-2 py-1 text-xs font-medium text-gray-700"
        >
          {formatTime(session.checkInAt)} - {formatTime(session.checkOutAt)}
        </span>
      ))}
    </div>
  );
}

function statusStyle(status) {
  const styles = {
    PRESENT: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    LATE: "bg-amber-50 text-amber-700 ring-amber-200",
    HALF_DAY: "bg-orange-50 text-orange-700 ring-orange-200",
    ABSENT: "bg-red-50 text-red-700 ring-red-200",
    ON_LEAVE: "bg-blue-50 text-blue-700 ring-blue-200",
    WEEKLY_OFF: "bg-violet-50 text-violet-700 ring-violet-200",
    HOLIDAY: "bg-violet-50 text-violet-700 ring-violet-200",
    ALREADY_SETTLED_OFF_CYCLE: "bg-gray-100 text-gray-600 ring-gray-200",
  };

  return styles[status] || "bg-gray-100 text-gray-600 ring-gray-200";
}

function statusLabel(status) {
  return String(status || "—")
    .replaceAll("_", " ")
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

export default function GeneratePayroll() {
  const navigate = useNavigate();
  const { accessToken } = useAuth();

  const [step, setStep] = useState(1);
  const [month, setMonth] = useState(previousIndiaMonth());

  const [preview, setPreview] = useState(null);
  const [payrollRun, setPayrollRun] = useState(null);
  const [advanceRecoveries, setAdvanceRecoveries] = useState({});

  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [expandedEmployeeId, setExpandedEmployeeId] = useState(null);
  const [error, setError] = useState("");
  const [employees, setEmployees] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [employeeFilters, setEmployeeFilters] = useState({});
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [candidateMonth, setCandidateMonth] = useState('');
  const [alreadyIncluded, setAlreadyIncluded] = useState(0);
  const [employeeRefresh, setEmployeeRefresh] = useState(0);
  const matchingEmployees = useMemo(() => filterPayrollEmployees(employees,employeeFilters,employeeSearch), [employees,employeeFilters,employeeSearch]);
  useEffect(() => {
    let cancelled = false;
    setEmployees([]); setSelectedIds([]); setCandidateMonth(''); setAlreadyIncluded(0);
    if (!accessToken || !/^\d{4}-\d{2}$/.test(month)) return;
    setLoadingEmployees(true); setError('');
    const [year, monthNumber] = month.split('-').map(Number);
    apiRequest(`/payroll/runs/employees?year=${year}&month=${monthNumber}`,{accessToken})
      .then(response => { if (!cancelled) { setEmployees(response.employees || []); setAlreadyIncluded(response.alreadyIncluded || 0); setCandidateMonth(month); } })
      .catch(error => { if (!cancelled) setError(error.message || 'Unable to load eligible employees'); })
      .finally(() => { if (!cancelled) setLoadingEmployees(false); });
    return () => { cancelled = true; };
  }, [accessToken,month,employeeRefresh]);

  const previewSummary = preview?.summary;
  const previewAdvanceRecoveryTotal = Object.values(advanceRecoveries).reduce(
    (total, recovery) =>
      total +
      (recovery?.employeeAdvanceId && recovery?.amount
        ? number(recovery.amount)
        : 0),
    0,
  );
  const previewNetAfterAdvanceRecovery = Math.max(
    0,
    number(previewSummary?.netPayable) - previewAdvanceRecoveryTotal,
  );

  const reviewPayroll = async () => {
    if (!accessToken) return;

    if (!month || !/^\d{4}-\d{2}$/.test(month)) {
      setError("Select a valid payroll month.");
      return;
    }
    if (loadingEmployees || candidateMonth !== month || !selectedIds.length) { setError('Select at least one eligible employee after the employee list loads.'); return; }

    const [yearText, monthText] = month.split("-");
    const year = Number(yearText);
    const monthNumber = Number(monthText);

    try {
      setIsLoadingPreview(true);
      setError("");
      setPreview(null);
      setPayrollRun(null);
      setAdvanceRecoveries({});
      setExpandedEmployeeId(null);

      const response = await apiRequest("/payroll/runs/preview", {
        method: "POST",
        accessToken,
        body: {
          year,
          month: monthNumber,
          employeeIds: selectedIds,
          advanceRecoveries: Object.entries(advanceRecoveries)
            .filter(
              ([, recovery]) => recovery.employeeAdvanceId && recovery.amount,
            )
            .map(([employeeId, recovery]) => ({
              employeeId,
              employeeAdvanceId: recovery.employeeAdvanceId,
              amount: Number(recovery.amount),
            })),
        },
      });

      setPreview(response);
      setStep(2);
    } catch (requestError) {
      const message = Array.isArray(requestError.details?.message)
        ? requestError.details.message.join(", ")
        : requestError.message;

      setError(message || "Unable to calculate payroll preview.");
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const generatePayroll = async () => {
    if (!accessToken) return;

    const [yearText, monthText] = month.split("-");
    const year = Number(yearText);
    const monthNumber = Number(monthText);

    try {
      setIsGenerating(true);
      setError("");

      const response = await apiRequest("/payroll/runs/generate", {
        method: "POST",
        accessToken,
        body: {
          year,
          month: monthNumber,
          employeeIds: (preview?.items || []).map(item => item.employee.id),
          advanceRecoveries: Object.entries(advanceRecoveries)
            .filter(
              ([, recovery]) =>
                recovery.employeeAdvanceId && Number(recovery.amount) > 0,
            )
            .map(([recoveryEmployeeId, recovery]) => ({
              employeeId: recoveryEmployeeId,
              employeeAdvanceId: recovery.employeeAdvanceId,
              amount: Number(recovery.amount),
            })),
        },
      });

      setPayrollRun(response);
      setStep(3);
    } catch (requestError) {
      const message = Array.isArray(requestError.details?.message)
        ? requestError.details.message.join(", ")
        : requestError.message;

      setError(message || "Unable to generate payroll draft.");
    } finally {
      setIsGenerating(false);
    }
  };

  const createdSummary = useMemo(() => {
    const items = payrollRun?.items || [];

    return {
      employees: items.length,
      gross: items.reduce((sum, item) => sum + number(item.grossEarnings), 0),
      deductions: items.reduce(
        (sum, item) => sum + number(item.totalDeductions),
        0,
      ),
      net: items.reduce((sum, item) => sum + number(item.netPayable), 0),
    };
  }, [payrollRun]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <p className="text-sm text-gray-500">HRMS / Payroll</p>

          <h1 className="mt-1 text-3xl font-bold text-gray-900">
            Generate Monthly Payroll
          </h1>

          <p className="mt-2 max-w-3xl text-gray-500">
            Preview attendance, actual worked hours, leaves, overtime,
            under-time, commissions, deductions, and employee payouts before
            creating a monthly payroll draft.
          </p>
        </div>

        <button
          type="button"
          onClick={() => navigate("/admindashboard/payroll")}
          className="inline-flex items-center gap-2 rounded-xl border border-gray-200 px-4 py-3 font-medium text-gray-700 transition hover:bg-gray-50"
        >
          <FiArrowLeft />
          Payroll Runs
        </button>
      </div>

      {error ? (
        <div className="flex gap-3 rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700">
          <FiAlertCircle className="mt-0.5 shrink-0" size={18} />
          <p>{error}</p>
        </div>
      ) : null}

      <div className="rounded-3xl border border-gray-100 bg-white p-6 shadow-sm sm:p-8">
        <PayrollStepper
          currentStep={step}
          steps={["Period", "Review", "Draft Created"]}
        />

        {step === 1 ? (
          <>
            <div className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900">
                Select payroll month
              </h2>

              <p className="mt-1 text-gray-500">
                Select employees for this batch. Generate the remaining employees later for the same month. Regular payroll can only be generated after the selected month ends.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">
                  Payroll month
                </label>

                <input
                  type="month"
                  value={month}
                  onChange={(event) => {
                    setMonth(event.target.value);
                    setPreview(null);
                  }}
                  className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 outline-none transition focus:border-primary-500 focus:ring-2 focus:ring-primary-100"
                />
              </div>

              <InfoBox
                icon={<FiUsers />}
                label="Employee scope"
                value={`${selectedIds.length} selected employees`}
              />

              <InfoBox
                icon={<FiFileText />}
                label="Calculation"
                value="Preview before draft"
              />
            </div>

            <div className="mt-6 space-y-4">
              <HrmsFilters records={employees} value={employeeFilters} onChange={setEmployeeFilters} fields={['branch','department','designation']} />
              <input aria-label="Search payroll employees" type="search" value={employeeSearch} onChange={event => setEmployeeSearch(event.target.value)} placeholder="Search name, nickname or employee code" className="w-full rounded-xl border border-gray-200 px-4 py-3 outline-none focus:border-primary-700" />
              <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
                <p>{selectedIds.length} selected · {employees.length} remaining eligible · {alreadyIncluded} already included in payroll. {selectedIds.filter(id => !matchingEmployees.some(employee => employee.id === id)).length} selected outside current filters.</p>
                <div className="flex gap-3"><button type="button" disabled={loadingEmployees} onClick={() => setSelectedIds(current => [...new Set([...current,...matchingEmployees.map(employee => employee.id)])])} className="font-semibold text-primary-800">Select all matching</button><button type="button" onClick={() => setSelectedIds([])} className="font-semibold text-gray-600">Clear selection</button></div>
              </div>
              <div className="max-h-80 overflow-auto rounded-xl border border-gray-200">
                <table className="w-full min-w-[650px] text-left text-sm"><thead className="sticky top-0 bg-primary-800 text-white"><tr>{['Select','Employee','Code','Branch','Department','Designation'].map(label => <th key={label} className="border-r border-primary-700 px-4 py-3">{label}</th>)}</tr></thead><tbody>
                  {matchingEmployees.map(employee => <tr key={employee.id} className="border-b border-gray-200 hover:bg-primary-50"><td className="px-4 py-3"><input type="checkbox" aria-label={`Include ${employee.fullName}`} checked={selectedIds.includes(employee.id)} onChange={event => setSelectedIds(current => event.target.checked ? [...new Set([...current,employee.id])] : current.filter(id => id !== employee.id))} /></td><td className="px-4 py-3">{employee.fullName}</td><td className="px-4 py-3">{employee.employeeCode}</td>{['branch','department','designation'].map(field => <td key={field} className="px-4 py-3">{employee.assignments?.[0]?.[field]?.name || '—'}</td>)}</tr>)}
                  {!matchingEmployees.length && <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-500">{loadingEmployees ? 'Loading employees…' : 'No remaining eligible employees match these filters.'}</td></tr>}
                </tbody></table>
              </div>
            </div>
            <div className="mt-8 rounded-3xl border border-primary-100 bg-primary-50 p-6">
              <h3 className="font-semibold text-primary-950">
                Calculation rules
              </h3>

              <ul className="mt-4 space-y-2 text-sm leading-6 text-primary-900">
                <li>
                  • No attendance and no approved leave is treated as absent.
                </li>
                <li>• Paid leave remains paid; unpaid leave is deducted.</li>
                <li>
                  • Worked time is compared against each employee’s shift.
                </li>
                <li>
                  • Overtime, under-time, holiday allowance, and POS commission
                  are included.
                </li>
                <li>
                  • Dates already paid in an off-cycle settlement are excluded.
                </li>
              </ul>
            </div>

            <div className="mt-10 flex justify-end">
              <button
                type="button"
                onClick={reviewPayroll}
                disabled={isLoadingPreview || loadingEmployees || !selectedIds.length || candidateMonth !== month}
                className="inline-flex items-center gap-2 rounded-2xl bg-primary-700 px-6 py-3 font-medium text-white transition hover:bg-primary-800 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <FiFileText />
                {isLoadingPreview ? "Calculating..." : "Calculate and Review"}
              </button>
            </div>
          </>
        ) : null}

        {step === 2 && preview ? (
          <>
            <div className="mb-8 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  Monthly payroll calculation review
                </h2>

                <p className="mt-1 max-w-3xl text-gray-500">
                  This is a preview only. No payroll run or employee payout has
                  been saved yet.
                </p>
              </div>

              <span className="inline-flex w-fit items-center gap-2 rounded-full bg-amber-50 px-4 py-2 text-sm font-semibold text-amber-700">
                <FiClock />
                Preview only
              </span>
            </div>

            <section className="overflow-hidden rounded-3xl border border-gray-200">
              <div className="bg-gray-950 px-6 py-6 text-white sm:px-8">
                <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
                  <div>
                    <p className="text-xs font-bold uppercase tracking-[0.16em] text-white/60">
                      Regular monthly payroll
                    </p>

                    <h3 className="mt-2 text-2xl font-bold">
                      {formatMonth(month)}
                    </h3>

                    <p className="mt-1 text-sm text-white/70">
                      {previewSummary?.employees || 0} active employees
                    </p>
                  </div>

                  <div className="rounded-2xl bg-white/10 px-5 py-4">
                    <p className="text-xs font-medium uppercase tracking-wide text-white/60">
                      Estimated net payroll
                    </p>

                    <p className="mt-1 text-3xl font-bold">
                      {money(previewSummary?.netPayable)}
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 divide-x divide-y divide-gray-100 bg-white md:grid-cols-4 md:divide-y-0">
                <ReviewMetric
                  label="Employees"
                  value={previewSummary?.employees || 0}
                  icon={<FiUsers />}
                />
                <ReviewMetric
                  label="Gross earnings"
                  value={money(previewSummary?.grossEarnings)}
                  icon={<FiTrendingUp />}
                />
                <ReviewMetric
                  label="Deductions"
                  value={money(previewSummary?.totalDeductions)}
                  icon={<FiTrendingDown />}
                />
                <ReviewMetric
                  label="Net payable"
                  value={money(previewNetAfterAdvanceRecovery)}
                  icon={<FiDollarSign />}
                />
              </div>
            </section>

            <section className="mt-8">
              <SectionTitle
                title="Employee calculations"
                description="Open any employee to audit salary, attendance, shift minutes, commissions, deductions, and daily payout."
              />

              <div className="space-y-4">
                {(preview.items || []).map((item) => (
                  <PayrollPreviewCard
                    key={item.employee?.id}
                    item={item}
                    expanded={expandedEmployeeId === item.employee?.id}
                    onToggle={() =>
                      setExpandedEmployeeId((current) =>
                        current === item.employee?.id
                          ? null
                          : item.employee?.id,
                      )
                    }
                  />
                ))}

                {!preview.items?.length ? (
                  <div className="rounded-3xl border border-dashed border-gray-300 bg-gray-50 py-14 text-center text-gray-500">
                    No eligible active employees were found for this payroll
                    period.
                  </div>
                ) : null}
              </div>
            </section>

            <PayrollPreviewAdvanceRecoverySetup
              items={preview.items || []}
              accessToken={accessToken}
              recoveries={advanceRecoveries}
              onChange={setAdvanceRecoveries}
            />

            <div className="mt-10 flex flex-col-reverse gap-3 sm:flex-row sm:justify-between">
              <button
                type="button"
                onClick={() => {
                  setStep(1);
                  setPreview(null);
                  setAdvanceRecoveries({});
                }}
                disabled={isGenerating}
                className="rounded-2xl border border-gray-200 px-6 py-3 font-medium text-gray-700 transition hover:bg-gray-50 disabled:opacity-50"
              >
                Edit Month
              </button>

              <button
                type="button"
                onClick={generatePayroll}
                disabled={isGenerating || !preview.items?.length}
                className="rounded-2xl bg-primary-700 px-6 py-3 font-medium text-white transition hover:bg-primary-800 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isGenerating
                  ? "Generating Draft..."
                  : "Confirm and Generate Draft"}
              </button>
            </div>
          </>
        ) : null}

        {step === 3 && payrollRun ? (
          <>
            <div className="rounded-3xl border border-green-200 bg-green-50 p-8 text-center">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-green-600 text-white">
                <FiCheckCircle size={28} />
              </div>

              <h2 className="mt-5 text-2xl font-bold text-gray-900">
                Monthly payroll draft created
              </h2>

              <p className="mx-auto mt-2 max-w-xl text-gray-600">
                Review the saved payroll run, add any permitted adjustments,
                then post it when it is approved.
              </p>
            </div>

            <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-4">
              <ReviewMetric
                label="Employees"
                value={createdSummary.employees}
                icon={<FiUsers />}
              />
              <ReviewMetric
                label="Gross earnings"
                value={money(createdSummary.gross)}
                icon={<FiTrendingUp />}
              />
              <ReviewMetric
                label="Deductions"
                value={money(createdSummary.deductions)}
                icon={<FiTrendingDown />}
              />
              <ReviewMetric
                label="Net payable"
                value={money(createdSummary.net)}
                icon={<FiDollarSign />}
              />
            </div>

            <div className="mt-10 flex justify-end">
              <button type="button" onClick={() => { setStep(1); setPreview(null); setPayrollRun(null); setAdvanceRecoveries({}); setSelectedIds([]); setCandidateMonth(''); setEmployeeFilters({}); setEmployeeSearch(''); setEmployeeRefresh(value => value + 1); }} className="mr-3 rounded-2xl border border-primary-200 px-6 py-3 font-medium text-primary-800">Generate another batch</button>
              <button
                type="button"
                onClick={() => navigate("/admindashboard/payroll")}
                className="rounded-2xl bg-primary-700 px-6 py-3 font-medium text-white transition hover:bg-primary-800"
              >
                Review Payroll Draft
              </button>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}

function PayrollPreviewCard({ item, expanded, onToggle }) {
  const [showDailyBreakdown, setShowDailyBreakdown] = useState(false);

  const employee = item.employee || {};
  const snapshot = item.calculationSnapshot || {};
  const dailyBreakdown = snapshot.dailyBreakdown || [];

  return (
    <article className="overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-sm">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center gap-4 p-5 text-left transition hover:bg-gray-50 sm:p-6"
      >
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-primary-50 font-bold text-primary-700">
          {employee.fullName
            ?.split(" ")
            .map((part) => part[0])
            .join("")
            .slice(0, 2)
            .toUpperCase()}
        </div>

        <div className="min-w-0 flex-1">
          <h3 className="truncate font-bold text-gray-900">
            {employee.fullName}
          </h3>

          <p className="mt-1 text-sm text-gray-500">
            {employee.employeeCode} ·{" "}
            {employee.salaryType === "DAILY"
              ? "Daily salary"
              : "Monthly salary"}
          </p>
        </div>

        <div className="hidden text-right sm:block">
          <p className="text-xs font-medium uppercase tracking-wide text-gray-400">
            Net payable
          </p>

          <p className="mt-1 text-lg font-bold text-primary-700">
            {money(item.netPayable)}
          </p>
        </div>

        <span className="text-gray-500">
          {expanded ? <FiChevronUp /> : <FiChevronDown />}
        </span>
      </button>

      <div className="flex justify-between border-t border-gray-100 bg-gray-50 px-5 py-3 sm:hidden">
        <span className="text-sm text-gray-500">Net payable</span>
        <span className="font-bold text-primary-700">
          {money(item.netPayable)}
        </span>
      </div>

      {expanded ? (
        <div className="border-t border-gray-200 bg-slate-50 p-5 sm:p-6">
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4 xl:grid-cols-8">
            <StatCard label="Payable days" value={snapshot.eligibleDays} />
            <StatCard label="Present" value={item.presentDays} tone="green" />
            <StatCard label="Late" value={item.lateDays} tone="amber" />
            <StatCard label="Half days" value={item.halfDays} tone="orange" />
            <StatCard
              label="Unpaid-day equivalent"
              value={item.absentDays}
              tone="red"
            />
            <StatCard
              label="Paid leave"
              value={item.paidLeaveDays}
              tone="blue"
            />
            <StatCard
              label="Unpaid leave"
              value={item.unpaidLeaveDays}
              tone="red"
            />
            <StatCard
              label="Holiday credits settled"
              value={`${number(item.holidayAllowanceUsed)} / ${number(
                item.monthlyHolidayAllowance,
              )}`}
              tone="purple"
            />
          </div>

          <div className="mt-5 grid grid-cols-1 gap-5 xl:grid-cols-3">
            <InfoPanel
              icon={<FiClock />}
              title="Shift and worked time"
              rows={[
                [
                  "Shift",
                  snapshot.shiftStartTime && snapshot.shiftEndTime
                    ? `${snapshot.shiftStartTime} – ${snapshot.shiftEndTime}`
                    : "No shift configured",
                ],
                [
                  "Expected work/month",
                  formatMinutes(
                    number(snapshot.expectedWorkingMinutes) *
                      number(snapshot.eligibleDays),
                  ),
                ],
                [
                  "Total work/month",
                  formatMinutes(
                    (snapshot.dailyBreakdown || []).reduce(
                      (total, day) => total + number(day.netWorkingMinutes),
                      0,
                    ),
                  ),
                ],
                ["Overtime worked", formatMinutes(item.overtimeMinutes)],
                ["Under-time", formatMinutes(item.earlyExitMinutes)],
                ["Daily salary rate", money(item.dailySalaryRate)],
              ]}
            />

            <InfoPanel
              icon={<FiTrendingUp />}
              title="Earnings"
              accent="green"
              rows={[
                ["Base salary", money(item.baseSalaryAmount)],
                [`Unused holiday payout (${number(snapshot.holidayEncashmentDays)} days)`, money(snapshot.holidayEncashmentAmount)],
                ["Overtime", money(item.overtimeAmount)],
                ["Commission earned", money(item.commissionEarnedAmount)],
                [
                  "Commission reversals",
                  `-${money(item.commissionReversedAmount)}`,
                ],
                ["Net commission", money(item.netCommissionAmount)],
                ["Gross earnings", money(item.grossEarnings)],
              ]}
            />

            <InfoPanel
              icon={<FiTrendingDown />}
              title="Deductions and payout"
              accent="red"
              rows={[
                [
                  `Absence (${number(item.unpaidAbsentDays)} days)`,
                  `-${money(item.absenceDeductionAmount)}`,
                ],
                [
                  `Unpaid leave (${number(item.unpaidLeaveDays)} days)`,
                  `-${money(item.unpaidLeaveDeductionAmount)}`,
                ],
                [
                  `Under-time (${formatMinutes(item.earlyExitMinutes)})`,
                  `-${money(item.earlyExitDeductionAmount)}`,
                ],
                ["Total deductions", `-${money(item.totalDeductions)}`],
                ["Final payable", money(item.netPayable)],
              ]}
            />
          </div>

          <div className="mt-5 overflow-hidden rounded-2xl border border-gray-200 bg-white">
            <button
              type="button"
              onClick={() => setShowDailyBreakdown((current) => !current)}
              className="flex w-full items-center justify-between gap-5 px-5 py-4 text-left transition hover:bg-gray-50"
            >
              <div>
                <h4 className="font-semibold text-gray-900">
                  Daily attendance and payroll calculation
                </h4>

                <p className="mt-1 text-sm text-gray-500">
                  Check-in/out, worked minutes, overtime, under-time, and
                  calculated daily payment.
                </p>
              </div>

              {showDailyBreakdown ? <FiChevronUp /> : <FiChevronDown />}
            </button>

            {showDailyBreakdown ? (
              <div className="overflow-x-auto border-t border-gray-200">
                <table className="min-w-[1080px] w-full text-left text-sm">
                  <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                    <tr>
                      <th className="px-5 py-4 font-semibold">Date</th>
                      <th className="px-5 py-4 font-semibold">Status</th>
                      <th className="px-5 py-4 font-semibold">Work sessions</th>
                      <th className="px-5 py-4 font-semibold">First / final</th>
                      <th className="px-5 py-4 font-semibold">Total worked</th>
                      <th className="px-5 py-4 font-semibold">Overtime</th>
                      <th className="px-5 py-4 font-semibold">Under-time</th>
                      <th className="px-5 py-4 text-right font-semibold">
                        Day pay
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-100">
                    {dailyBreakdown.map((day) => (
                      <tr key={day.date} className="hover:bg-gray-50">
                        <td className="px-5 py-4 font-medium text-gray-900">
                          {formatDate(day.date)}
                        </td>

                        <td className="px-5 py-4">
                          <span
                            className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ${statusStyle(
                              day.status,
                            )}`}
                          >
                            {statusLabel(day.status)}
                          </span>
                        </td>

                        <td className="px-5 py-4">
                          <SessionTimeline sessions={day.sessions} />
                        </td>

                        <td className="px-5 py-4 text-gray-700">
                          <div>{formatTime(day.checkInAt)}</div>
                          <div className="mt-1 text-xs text-gray-400">
                            Final: {formatTime(day.checkOutAt)}
                          </div>
                        </td>

                        <td className="px-5 py-4 font-medium text-gray-900">
                          {day.netWorkingMinutes === null
                            ? "—"
                            : formatMinutes(day.netWorkingMinutes)}
                        </td>

                        <td className="px-5 py-4 text-emerald-700">
                          {formatMinutes(day.overtimeMinutes)}
                        </td>

                        <td className="px-5 py-4 text-red-700">
                          {formatMinutes(
                            day.underTimeMinutes ?? day.earlyExitMinutes,
                          )}
                        </td>

                        <td className="px-5 py-4 text-right font-bold text-gray-900">
                          <div>{money(day.attendancePayableAmount)}</div>
                          {day.holidayEntitlementApplied ? (
                            <p className="mt-1 text-[11px] font-semibold text-primary-700">
                              Policy-paid absence
                            </p>
                          ) : null}
                        </td>
                      </tr>
                    ))}

                    {!dailyBreakdown.length ? (
                      <tr>
                        <td
                          colSpan={8}
                          className="px-5 py-10 text-center text-gray-500"
                        >
                          No daily payroll details are available.
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </article>
  );
}

function SectionTitle({ title, description }) {
  return (
    <div className="mb-5">
      <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
      <p className="mt-1 text-sm text-gray-500">{description}</p>
    </div>
  );
}

function StatCard({ label, value, tone = "gray" }) {
  const styles = {
    gray: "border-gray-100 bg-gray-50 text-gray-900",
    green: "border-emerald-100 bg-emerald-50 text-emerald-800",
    amber: "border-amber-100 bg-amber-50 text-amber-800",
    orange: "border-orange-100 bg-orange-50 text-orange-800",
    red: "border-red-100 bg-red-50 text-red-800",
    blue: "border-blue-100 bg-blue-50 text-blue-800",
    purple: "border-violet-100 bg-violet-50 text-violet-800",
  };

  return (
    <div className={`rounded-2xl border p-4 ${styles[tone]}`}>
      <p className="text-xs font-medium opacity-70">{label}</p>
      <p className="mt-2 text-xl font-bold">{value ?? 0}</p>
    </div>
  );
}

function InfoBox({ icon, label, value }) {
  return (
    <div className="rounded-2xl border border-gray-100 bg-gray-50 p-5">
      <div className="flex items-center gap-2 text-primary-700">
        {icon}
        <p className="text-sm font-medium text-gray-600">{label}</p>
      </div>

      <p className="mt-3 font-semibold text-gray-900">{value}</p>
    </div>
  );
}

function ReviewMetric({ label, value, icon }) {
  return (
    <div className="flex items-start gap-3 px-5 py-5">
      <div className="rounded-xl bg-primary-50 p-2.5 text-primary-700">
        {icon}
      </div>

      <div>
        <p className="text-sm text-gray-500">{label}</p>
        <p className="mt-1 font-semibold text-gray-900">{value}</p>
      </div>
    </div>
  );
}

function InfoPanel({ icon, title, rows, accent = "primary" }) {
  const styles = {
    primary: "border-gray-200 text-primary-700",
    green: "border-emerald-100 text-emerald-700",
    red: "border-red-100 text-red-700",
  };

  return (
    <div className={`rounded-3xl border bg-white p-6 ${styles[accent]}`}>
      <div className="flex items-center gap-3">
        <div className="rounded-xl bg-gray-50 p-2.5">{icon}</div>
        <h4 className="font-semibold text-gray-900">{title}</h4>
      </div>

      <div className="mt-5 space-y-3">
        {rows.map(([label, value]) => (
          <div
            key={label}
            className="flex items-start justify-between gap-4 text-sm"
          >
            <span className="text-gray-500">{label}</span>
            <span className="text-right font-semibold text-gray-900">
              {value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
